import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import type { TokenDataWithCreator, TokenCategory } from '../../types';
import { useTokens } from '../../hooks/useTokens';
import { useTokenFavorites } from '../../hooks/useUsers';
import { formatMarketCap, getLastFourChars } from '../../utils/address';
import { useAccount } from 'wagmi';
import TradeModal from '../shared/modals/TradeModal';
import SolanaTradeModal from '../shared/modals/SolanaTradeModal';
import { SolanaWalletProvider } from '../SolanaWalletProvider';
import { ZNSAddressDisplay } from '../shared/ZNSAddressDisplay';
import { sanitizeUrl } from '../../utils/sanitizeUrl';
import { fetchTokensByChain, type UnifiedToken } from '../../utils/unifiedTokenFetcher';

// Helper function to convert HTTP URLs to HTTPS for mixed content security
const ensureHttpsUrl = (url: string | null | undefined, fallback: string): string => {
  if (!url) return fallback;

  // Handle IPFS hashes (like the one in the error)
  if (url.startsWith('Qm') && url.length === 46) {
    return `https://ipfs.io/ipfs/${url}`;
  }

  // Handle ImgBB page URLs - convert to direct image URL
  if (url.includes('ibb.co/')) {
    const match = url.match(/ibb\.co\/([a-zA-Z0-9]+)/);
    if (match) {
      return `https://i.ibb.co/${match[1]}.jpg`;
    }
  }

  // Handle img.bb URLs - keep HTTP for img.bb as they may not support HTTPS
  if (url.includes('img.bb')) {
    return url.startsWith('http://') ? url : `http://${url}`;
  }

  // Convert other HTTP URLs to HTTPS
  if (url.startsWith('http://')) {
    return url.replace('http://', 'https://');
  }

  if (!url.startsWith('http')) {
    return url; // Assume it's a relative path or already HTTPS
  }

  return url;
};

// Helper function to format price with subscript for small values
const formatPriceWithSubscript = (price: number | null): React.ReactNode => {
  if (!price) return 'N/A';

  if (price >= 0.01) {
    return `$${price.toFixed(6)}`;
  }

  // For very small values, use subscript notation
  const priceStr = price.toFixed(20);
  const match = priceStr.match(/^0\.0*([1-9])/);

  if (match) {
    const zeros = priceStr.indexOf(match[1]) - 2;
    const significantDigits = priceStr.slice(priceStr.indexOf(match[1]), priceStr.indexOf(match[1]) + 2);
    return (
      <>
        $0.0<sub>{zeros}</sub>{significantDigits}
      </>
    );
  }

  return `$${price.toFixed(6)}`;
};
// import { supabase, TABLES } from '../../utils/supabase'; // No longer needed after optimization
// import { useTVL } from '../../hooks/useTVL';
import { useRealtimePrices, formatPriceChange, getPriceChangeColor } from '../../hooks/useRealtimePrices';
import { getRelayTokenPriceSolana } from '../../hooks/useRelayPrice';
// import { useNovesPrices } from '../../hooks/useNovesPrices';
// import { useMarketCap } from '../../hooks/useMarketCap';

const TokenListContainer = styled.div`
  background: transparent;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 15px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #9535fc;
  @media (max-width: 600px) {
    gap: 8px;
    margin-bottom: 8px;
    padding-bottom: 5px;
  }
`;

const HeaderTopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  @media (max-width: 600px) {
    gap: 6px;
  }
`;

const FilterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
  
  @media (max-width: 600px) {
    gap: 8px;
  }
`;

const CategoryFilterContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  align-items: center;
  
  @media (max-width: 600px) {
    gap: 4px;
  }
`;

const CategoryFilterScroll = styled.div`
  display: flex;
  gap: 8px;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
  flex: 1;
  
  /* Hide scrollbar but keep functionality */
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar {
    display: none;
  }
  
  @media (max-width: 600px) {
    gap: 4px;
  }
`;

const SectionTitle = styled.h2`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-weight: 300;
  font-size: 20px;
  color: #fffeff;
  margin: 0;
  letter-spacing: 1px;
  text-shadow: 0 0 20px rgba(255, 255, 255, 0.2);
  @media (max-width: 600px) {
    font-size: 15px;
    letter-spacing: 0.5px;
  }
`;

const FilterToggle = styled.div`
  display: flex;
  gap: 8px;
  margin-left: auto;
  align-items: center;
  
  @media (max-width: 600px) {
    gap: 4px;
  }
`;

const FilterLabel = styled.span`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 12px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.7);
  white-space: nowrap;
  
  @media (max-width: 600px) {
    font-size: 10px;
  }
`;

const FilterButton = styled.button<{ $active: boolean }>`
  padding: 6px 12px;
  background: ${props => props.$active ? '#000000' : 'var(--color-bg-card, #000000)'};
  border: 1px solid ${props => props.$active ? '#444444' : 'var(--color-border, #333333)'};
  border-radius: 6px;
  color: ${props => props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 12px;
  font-weight: ${props => props.$active ? '600' : '400'};
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 6px;
  box-shadow: ${props => props.$active ? '0 0 15px #333333' : 'none'};

  &:hover {
    background: ${props => props.$active ? '#000000' : 'var(--color-bg-hover, #000000)'};
    border-color: #444444;
    color: #ffffff;
  }

  @media (max-width: 600px) {
    padding: 4px 8px;
    font-size: 10px;
    gap: 4px;
  }
`;

const FilterButtonLogo = styled.img`
  height: 16px;
  width: auto;
  object-fit: contain;
  
  @media (max-width: 600px) {
    height: 14px;
  }
`;

const SearchContainer = styled.div`
  margin-bottom: 20px;
`;

const SearchInput = styled.input`
  background: #000000;
  border: 2px solid #9535fc;
  border-radius: 3px;
  padding: 8px 10px;
  color: #fffeff;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  width: 100%;
  box-sizing: border-box;
  outline: none;
  transition: border-color 0.1s linear, box-shadow 0.1s linear;
  box-shadow: 2px 2px #fffeff;

  &:focus {
    border-color: #fffeff;
    box-shadow: 3px 3px #9535fc;
  }

  &::placeholder {
    color: #777777;
  }
  @media (max-width: 600px) {
    padding: 6px 7px;
    font-size: 12px;
  }
`;

const ViewToggle = styled.div`
  display: inline-flex;
  gap: 8px;
  background: transparent;
  border: none;
`;

const ViewButton = styled.button<{ $active: boolean }>`
  padding: 8px 16px;
  background: ${props => props.$active ? '#000000' : '#000000'};
  border: 1px solid ${props => props.$active ? 'var(--color-neon-purple)' : '#333333'};
  border-radius: 8px;
  color: ${props => props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.6)'};
  font-size: 13px;
  font-weight: ${props => props.$active ? '600' : '300'};
  font-family: 'Plus Jakarta Sans', sans-serif;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;

  &:hover {
    background: #000000;
    border-color: var(--color-neon-purple);
    color: #ffffff;
  }

  @media (max-width: 600px) {
    padding: 6px 12px;
    font-size: 11px;
  }
`;

const TokenListTable = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  padding-top: 10px;
  width: 100%;
`;

const TokenListRow = styled.div`
  display: grid;
  grid-template-columns: 40px 40px 1fr 120px 100px 80px 80px;
  gap: 12px;
  padding: 14px 16px;
  background: var(--color-bg-card, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 10px;
  align-items: center;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  
  &:hover {
    background: var(--color-bg-hover, #000000);
    border-color: #333333;
    transform: translateX(4px);
    box-shadow: 0 0 20px #000000;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 24px 28px 1.2fr 60px 50px 45px 35px;
    gap: 3px;
    padding: 10px 4px;
    font-size: 10px;
  }
`;

const TokenListHeader = styled.div`
  display: grid;
  grid-template-columns: 40px 40px 1fr 120px 100px 80px 80px;
  gap: 12px;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 700;
  color: rgba(255, 255, 255, 0.6);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.8px;
  text-align: center;
  
  .mobile-only {
    display: none;
  }
  
  .desktop-only {
    display: inline;
  }
  
  & > div:first-child,
  & > div:nth-child(3) {
    text-align: left;
  }
  
  & > div:nth-child(4),
  & > div:nth-child(5) {
    text-align: right;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 24px 28px 1.2fr 60px 50px 45px 35px;
    gap: 3px;
    padding: 10px 4px;
    font-size: 8px;
    letter-spacing: 0.2px;
    
    .mobile-only {
      display: inline;
    }
    
    .desktop-only {
      display: none;
    }
  }
`;

const TokenRankCell = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--color-neon-purple);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-shadow: 0 0 8px rgba(188, 19, 254, 0.4);
  
  @media (max-width: 768px) {
    font-size: 11px;
  }
`;

const TokenListLogo = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
  border: 1px solid #333333;
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.4) 0%, #333333 100%);
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
  }
`;

const TokenListInfoCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TokenListName = styled.div`
  font-size: 14px;
  font-weight: 300;
  color: #ffffff;
  font-family: 'Plus Jakarta Sans', sans-serif;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  
  @media (max-width: 768px) {
    font-size: 12px;
  }
`;

const TokenListSymbol = styled.div`
  font-size: 12px;
  font-weight: 500;
  color: var(--color-neon-blue);
  font-family: 'Plus Jakarta Sans', sans-serif;
  
  @media (max-width: 768px) {
    font-size: 10px;
  }
`;

const TokenListDataCell = styled.div`
  font-size: 13px;
  font-weight: 300;
  color: rgba(255, 255, 255, 0.8);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-align: center;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    font-size: 8px;
  }
`;

const TokenListValueCell = styled.div`
  font-size: 14px;
  font-weight: 700;
  color: var(--color-neon-green);
  font-family: 'Plus Jakarta Sans', sans-serif;
  text-align: right;
  text-shadow: 0 0 10px rgba(10, 255, 10, 0.3);
  
  @media (max-width: 768px) {
    font-size: 8px;
    white-space: nowrap;
    overflow: visible;
  }
`;

const TokenGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 24px;
  padding-top: 10px;
  width: 100%;
  overflow: hidden;
  
  @media (max-width: 1600px) {
    grid-template-columns: repeat(3, 1fr);
  }
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 18px;
  }
  @media (max-width: 700px) {
    gap: 20px;
    padding-bottom: 16px;
  }
`;

// Redesigned TokenCard with refined spacing and hierarchy
const TokenCard = styled.div`
  background: var(--color-bg-card, #000000);
  border: 1px solid var(--color-border, #333333);
  border-radius: 16px;
  padding: 18px;
  width: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  transition: all 0.3s ease;
  position: relative;
  margin-bottom: 0;
  box-sizing: border-box;
  gap: 14px;
  min-height: 165px;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, #444444, transparent);
  }
  
  &:hover {
    background: var(--color-bg-hover, #000000);
    border-color: #333333;
    transform: translateY(-4px);
    box-shadow: 0 0 30px #333333;
  }
  
  @media (max-width: 700px) {
    padding: 16px;
    margin-bottom: 0;
    min-height: 155px;
  }
`;

// Refined card content layout with better spacing
const TokenCardContent = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  width: 100%;
  padding: 0;
  gap: 16px;
  
  @media (max-width: 600px) {
    gap: 12px;
  }
`;

const TokenLogoClickable = styled.img`
  width: 64px;
  height: 64px;
  border-radius: 14px;
  object-fit: cover;
  border: 2px solid #333333;
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.4) 0%, #333333 100%);
  cursor: pointer;
  transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  margin: 0;
  flex-shrink: 0;
  
  &:hover {
    transform: scale(1.08);
    border-color: #444444;
    box-shadow: 0 4px 16px rgba(88, 28, 135, 0.3);
  }
  
  @media (max-width: 600px) {
    width: 56px;
    height: 56px;
  }
`;

const TokenInfoWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  margin-left: 0;
  justify-content: flex-start;
  gap: 6px;
  min-width: 0; /* Allow text truncation */
`;

// Update TokenNameRow to be a flex row with space-between
const TokenNameRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  
  @media (max-width: 600px) {
    flex-wrap: wrap;
    gap: 8px;
  }
`;

// Remove margin-right/max-width from TokenName, handle truncation in a span
// const TokenName = styled.div`
//   font-family: 'Inter', sans-serif;
//   font-weight: 700;
//   font-size: 15px;
//   color: #fffeff;
//   display: flex;
//   align-items: center;
//   gap: 5px;
// `;

const NetworkLogo = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
  vertical-align: middle;
  background: transparent;
`;

const TokenSymbol = styled.div`
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 13px;
  color: var(--color-neon-blue);
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 4px;
  letter-spacing: 0.02em;
`;

// const TokenMetrics = styled.div`
//   display: flex;
//   flex-direction: column;
//   gap: 2px;
//   margin-top: 4px;
// `;

// const MetricRow = styled.div`
//   display: flex;
//   justify-content: space-between;
//   align-items: center;
//   font-size: 11px;
//   color: #bdbdbd;
// `;

// const MetricLabel = styled.span`
//   color: #888;
// `;

// const MetricValue = styled.span`
//   color: #fffeff;
//   font-weight: 500;
// `;

// const TokenContract = styled.div`
//   font-family: 'Inter', sans-serif;
//   font-size: 13px;
//   color: #bdbdbd;
//   word-break: break-all;
//   display: flex;
//   align-items: center;
//   gap: 6px;
// `;

const CopyButton = styled.button`
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.4);
  cursor: pointer;
  margin-left: 0;
  padding: 4px;
  font-size: 1em;
  transition: all 0.2s ease;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    color: #a855f7;
    background: #000000;
  }
`;

const TokenLinks = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 16px;
`;

const TokenLinkIcon = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: #000000;
  border: 1px solid #333333;
  border-radius: 7px;
  color: rgba(255, 255, 255, 0.7);
  text-decoration: none;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  font-size: 13px;
  
  &:hover {
    background: #333333;
    border-color: #444444;
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(88, 28, 135, 0.2);
  }
`;

const ExplorerIcon = styled.img`
  width: 15px;
  height: 15px;
  object-fit: contain;
  vertical-align: middle;
  transition: all 0.2s ease;
`;

const DextoolsIcon = styled.img`
  width: 15px;
  height: 15px;
  object-fit: contain;
  filter: brightness(0) invert(1);
  transition: all 0.2s ease;
`;

// Note: Removed DexScreenerIcon since we no longer use DexScreener

// const TokenHeader = styled.div`
//   display: flex;
//   align-items: center;
//   gap: 10px;
//   margin-bottom: 10px;
// `;

// const TokenLogo = styled.img`
//   width: 50px;
//   height: 50px;
//   border-radius: 50%;
//   object-fit: cover;
//   border: 3px solid #0c0814;
//   background-color: #0c0814;
//   image-rendering: pixelated;
// `;

// const TokenInfo = styled.div`
//   flex-grow: 1;
// `;

const SymbolContractRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 0;
  margin-bottom: 2px;
  flex-wrap: wrap;
`;


// const BuyButton = styled.a`
//   background: #9535fc;
//   color: #fff;
//   padding: 8px 0;
//   border-radius: 6px;
//   font-family: 'Inter', sans-serif;
//   font-weight: 700;
//   font-size: 15px;
//   text-decoration: none;
//   text-align: center;
//   transition: all 0.1s linear;
//   border: 2px solid #9535fc;
//   box-shadow: 0 2px 8px #9535fc44;
//   width: 100%;
//   margin-top: auto;
//   letter-spacing: 0.5px;
//   align-self: flex-end;
//   &:hover {
//     opacity: 0.93;
//     background: #b66bff;
//     color: #0c0814;
//     border: 2px solid #b66bff;
//   }
// `;

const LoadingMessage = styled.div`
  text-align: center;
  color: #9535fc;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  padding: 20px;
`;

const ErrorMessage = styled.div`
  text-align: center;
  color: #FF5555;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  padding: 20px;
  background: rgba(255, 0, 0, 0.1);
  border: 1px dashed #FF5555;
  border-radius: 5px;
`;

// Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0,0,0,0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;
// const ModalContent = styled.div`
//   background: #18141f;
//   border-radius: 14px;
//   padding: 32px 32px 24px 32px;
//   min-width: 320px;
//   max-width: 95vw;
//   max-height: 90vh;
//   overflow-y: auto;
//   box-shadow: 0 8px 32px #9535fc55;
//   display: flex;
//   flex-direction: column;
//   align-items: center;
// `;
const ModalClose = styled.button`
  position: absolute;
  top: 18px;
  right: 24px;
  background: none;
  border: none;
  color: #fff;
  font-size: 1.5em;
  cursor: pointer;
  &:hover {
    color: #9535fc;
  }
`;

const ModalTokenName = styled.div`
  font-family: 'Inter', sans-serif;
  font-size: 22px;
  color: #fffeff;
  margin-bottom: 8px;
  display: flex;
  align-items: center;
  gap: 10px;
`;
const ModalNetworkLogo = styled.img`
  width: 28px;
  height: 28px;
  object-fit: contain;
  vertical-align: middle;
  background: transparent;
`;
const ModalTokenSymbol = styled.div`
  font-size: 16px;
  color: #9535fc;
  font-weight: 600;
  margin-bottom: 8px;
`;
// const ModalTokenContract = styled.div`
//   font-size: 15px;
//   color: #fffeff;
//   margin-bottom: 8px;
//   display: flex;
//   align-items: center;
//   gap: 6px;
// `;

const ViewDetailsButton = styled.button`
  background: #000000;
  color: rgba(255, 255, 255, 0.7);
  border: 1px solid #333333;
  width: 28px;
  height: 28px;
  border-radius: 7px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 13px;
  padding: 0;
  margin-left: 0;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  
  &:hover {
    background: #333333;
    border-color: #444444;
    color: #ffffff;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(88, 28, 135, 0.2);
  }
`;

// ON DEX Badge styled component
const OnDexBadge = styled.div`
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  padding: 2px 6px;
  border-radius: 4px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 4px rgba(16, 185, 129, 0.3);
`;

const FavoriteButton = styled.button<{ $isFavorited: boolean }>`
  background: ${props => props.$isFavorited ? '#333333' : 'transparent'};
  border: 1px solid ${props => props.$isFavorited ? '#333333' : 'transparent'};
  color: ${props => props.$isFavorited ? '#a855f7' : 'rgba(255, 255, 255, 0.4)'};
  padding: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 28px;
  height: 28px;
  border-radius: 7px;
  
  &:hover {
    color: #a855f7;
    background: #333333;
    border-color: #333333;
    transform: scale(1.05);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const TokenNameSpan = styled.span`
  font-family: 'Inter', sans-serif;
  font-size: 17px;
  font-weight: 700;
  color: #ffffff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
  margin-right: 8px;
  max-width: 180px;
  letter-spacing: -0.02em;
  
  @media (max-width: 600px) {
    font-size: 15px;
    max-width: 120px;
  }
`;

const QuickBuyButton = styled.button`
  background: linear-gradient(135deg, rgba(147, 51, 234, 0.9) 0%, rgba(168, 85, 247, 0.9) 100%);
  border: 1px solid #333333;
  color: #ffffff;
  padding: 6px 14px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 48px;
  height: 28px;
  border-radius: 8px;
  font-family: 'Inter', sans-serif;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 2px 8px rgba(88, 28, 135, 0.2);
  
  &:hover {
    background: linear-gradient(135deg, rgba(168, 85, 247, 1) 0%, rgba(147, 51, 234, 1) 100%);
    border-color: rgba(168, 85, 247, 0.6);
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(88, 28, 135, 0.4);
  }
  
  &:active {
    transform: translateY(0);
  }
  
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

// Removed CreatorInfo, CreatorLink, and FavoritesCount styled components
// as creator info is now displayed underneath token logo

// Add TVLLine styled component
// const TVLLine = styled.div`
//   font-family: 'Inter', sans-serif;
//   font-size: 18px;
//   color: #fff;
//   margin: 2px 0 0 0;
//   font-weight: 500;
// `;

// Add styled components for modal banner and logo
const ModalBanner = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  display: block;
  image-rendering: pixelated;
`;
const ModalLogoContainer = styled.div`
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin-top: -36px;
  margin-left: 24px;
  margin-bottom: 12px;
  z-index: 2;
  background: none;
  position: relative;
  @media (max-width: 700px) {
    margin-left: 8px;
    margin-top: -24px;
  }
`;
const ModalLogo = styled.img`
  width: 72px;
  height: 72px;
  border-radius: 12px;
  object-fit: cover;
  border: 4px solid #000000;
  background-color: #000000;
  image-rendering: pixelated;
`;
// Update ModalContentStyled to match QuickBuy/QuickSell modal styling
const ModalContentStyled = styled.div`
  background: #000000;
  border-radius: 20px;
  border: 1px solid #333333;
  padding: 32px 32px 24px 32px;
  min-width: 320px;
  max-width: 600px;
  width: 90vw;
  max-height: 85vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  @media (max-width: 700px) {
    max-height: 85vh;
    padding: 12px 10px;
    min-width: 280px;
    max-width: 95vw;
    width: 95vw;
  }
`;
// Update ModalDetails for fixed width and box-sizing
const ModalDetails = styled.div`
  padding: 60px 32px 0 32px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  @media (max-width: 700px) {
    padding: 40px 16px 0 16px;
  }
`;
// Update ModalDescriptionBox for more horizontal padding and margin
const ModalDescriptionBox = styled.div`
  background: #000000;
  padding: 12px 22px;
  border-radius: 6px;
  color: #bdbdbd;
  font-size: 15px;
  margin: 18px 0 12px 0;
  width: calc(100% - 8px);
  box-sizing: border-box;
`;

// Add ModalMarketDataBox styled component to match ModalDescriptionBox width
const ModalMarketDataBox = styled.div`
  background: #000000;
  padding: 16px;
  border-radius: 8px;
  margin: 16px 0;
  border: 1px solid #9535fc;
  width: calc(100% - 8px);
  box-sizing: border-box;
`;

const ModalTokenContractAddress = styled.div`
  color: #bdbdbd;
  font-size: 14px;
  margin: 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ModalTokenLinks = styled.div`
  display: flex;
  gap: 12px;
  margin-top: 16px;
  flex-wrap: wrap;
`;

// Pagination Components
const PaginationContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 10px;
  margin-top: 30px;
  padding: 20px 0;
  flex-wrap: wrap;
  
  @media (max-width: 768px) {
    gap: 6px;
    padding: 16px 8px;
  }
`;

const PaginationButton = styled.button`
  background: ${props => props.disabled ? '#000000' : '#000000'};
  border: 2px solid ${props => props.disabled ? '#555' : '#9535fc'};
  border-radius: 6px;
  color: ${props => props.disabled ? '#666' : '#fffeff'};
  padding: 8px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  min-width: 40px;
  
  &:hover:not(:disabled) {
    background: #9535fc;
    border-color: #b66bff;
    box-shadow: 0 2px 8px rgba(149, 53, 252, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 12px;
    min-width: 36px;
  }
`;

const PageNumberButton = styled.button<{ $active?: boolean }>`
  background: ${props => props.$active ? '#9535fc' : '#000000'};
  border: 2px solid ${props => props.$active ? '#b66bff' : '#9535fc'};
  border-radius: 6px;
  color: ${props => props.$active ? '#fffeff' : '#fffeff'};
  padding: 8px 12px;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  min-width: 40px;
  
  &:hover {
    background: ${props => props.$active ? '#b66bff' : '#9535fc'};
    border-color: #b66bff;
    box-shadow: 0 2px 8px rgba(149, 53, 252, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 12px;
    min-width: 36px;
  }
`;

const PaginationInfo = styled.div`
  color: #bdbdbd;
  font-family: 'Inter', sans-serif;
  font-size: 14px;
  margin: 0 20px;
  
  @media (max-width: 768px) {
    font-size: 11px;
    margin: 0 8px;
    width: 100%;
    text-align: center;
    order: -1;
    margin-bottom: 8px;
  }
`;

const PageNumbers = styled.div`
  display: flex;
  gap: 5px;
  align-items: center;
  
  @media (max-width: 768px) {
    gap: 4px;
  }
`;


export const TokenSearchBar: React.FC<{ searchQuery: string; setSearchQuery: (q: string) => void }> = ({ searchQuery, setSearchQuery }) => (
  <SearchContainer>
    <SearchInput
      type="text"
      placeholder="SEARCH TOKENS BY NAME OR CONTRACT ADDRESS..."
      value={searchQuery}
      onChange={(e) => setSearchQuery(e.target.value)}
    />
  </SearchContainer>
);

// TokenList component - chain filter is now controlled by parent
export const TokenList: React.FC<{ searchQuery: string; chainFilter?: 'ink' | 'eth' | 'wlfi' | 'mon' | 'sol' }> = ({ searchQuery, chainFilter = 'ink' }) => {
  const { address, isConnected } = useAccount();
  const navigate = useNavigate();
  const [viewMode, setViewMode] = useState<'cards' | 'list'>('cards');
  const [categoryFilter, setCategoryFilter] = useState<TokenCategory | 'all'>('all');
  const [dexFilter, setDexFilter] = useState<'all' | 'Protofire' | 'InkySwap'>('all');
  
  // Use useTokens for Ink (backward compatibility), fetchTokensByChain for other chains
  const inkTokensResult = useTokens(searchQuery, categoryFilter);
  const [multiChainTokens, setMultiChainTokens] = useState<UnifiedToken[]>([]);
  const [multiChainLoading, setMultiChainLoading] = useState(false);
  const [multiChainError, setMultiChainError] = useState<string | null>(null);
  
  // Fetch tokens for non-Ink chains
  useEffect(() => {
    if (chainFilter === 'ink') {
      // Use existing hook for Ink
      setMultiChainTokens([]);
      setMultiChainLoading(false);
      setMultiChainError(null);
      return;
    }
    
    const fetchMultiChainTokens = async () => {
      setMultiChainLoading(true);
      setMultiChainError(null);
      try {
        console.log(`[TokenList] Fetching ${chainFilter} tokens with searchQuery:`, searchQuery);
        const fetchedTokens = await fetchTokensByChain(chainFilter, {
          searchQuery,
          limit: 100,
          orderBy: 'created_at',
          ascending: false
        });
        console.log(`[TokenList] Fetched ${fetchedTokens.length} ${chainFilter} tokens`);
        setMultiChainTokens(fetchedTokens);
      } catch (error) {
        console.error(`[TokenList] Error fetching ${chainFilter} tokens:`, error);
        setMultiChainError(error instanceof Error ? error.message : 'Failed to fetch tokens');
      } finally {
        setMultiChainLoading(false);
      }
    };
    
    fetchMultiChainTokens();
  }, [chainFilter, searchQuery]);
  
  // Determine which tokens to use based on chain filter
  const tokens = chainFilter === 'ink' 
    ? inkTokensResult.tokens 
    : multiChainTokens.map(t => ({
        ...t,
        id: t.id || t.contract_address,
        network: (t.network || (chainFilter === 'mon' ? 'monad' : 'eth')) as 'ink' | 'monad' | 'eth',
        deployer_address: t.deployer_address || '',
        favorites_count: t.favorites_count || 0,
      })) as typeof inkTokensResult.tokens;
  
  const tokensLoading = chainFilter === 'ink' ? inkTokensResult.loading : multiChainLoading;
  const tokensError = chainFilter === 'ink' ? inkTokensResult.error : multiChainError;
  const currentPage = chainFilter === 'ink' ? inkTokensResult.currentPage : 1;
  const totalPages = chainFilter === 'ink' ? inkTokensResult.totalPages : 1;
  const goToPage = chainFilter === 'ink' ? inkTokensResult.goToPage : () => {};
  const goToNextPage = chainFilter === 'ink' ? inkTokensResult.goToNextPage : () => {};
  const goToPreviousPage = chainFilter === 'ink' ? inkTokensResult.goToPreviousPage : () => {};
  
  const [swapModalOpen, setSwapModalOpen] = useState(false);
  const [solanaSwapModalOpen, setSolanaSwapModalOpen] = useState(false);
  const [swapToken, setSwapToken] = useState<TokenDataWithCreator | null>(null);
  const { userFavorites, toggleFavorite } = useTokenFavorites(isConnected ? address : undefined);
  const [tokensWithDetails, setTokensWithDetails] = useState<TokenDataWithCreator[]>([]);
  
  // Solana-specific price state (same as SolanaTokenList)
  const [solanaTokenPrices, setSolanaTokenPrices] = useState<Map<string, number>>(new Map());
  const [solanaPricesLoading, setSolanaPricesLoading] = useState(false);

  // const { tvlData } = useTVL();
  // Map chain filter to chain ID for price fetching
  const chainIdMap: Record<string, number> = {
    'ink': 57073,
    'eth': 1,
    'wlfi': 1,
    'mon': 143,
    'sol': 792703809  // Relay's Solana chain ID
  };
  const chainId = chainIdMap[chainFilter] || 57073;
  const { getPriceForToken, get24hChangeForToken, loading: pricesLoading } = useRealtimePrices(chainId);
  
  // Fetch Solana token prices using Relay API (same as SolanaTokenList)
  useEffect(() => {
    if (chainFilter !== 'sol' || tokens.length === 0) {
      setSolanaTokenPrices(new Map());
      setSolanaPricesLoading(false);
      return;
    }

    const fetchSolanaPrices = async () => {
      setSolanaPricesLoading(true);
      const pricesMap = new Map<string, number>();

      // Fetch prices with rate limiting (same as SolanaTokenList)
      for (const token of tokens) {
        const mintAddress = (token as any).mint_address || token.contract_address;
        if (!mintAddress) continue;

        try {
          const price = await getRelayTokenPriceSolana(mintAddress);
          if (price && price > 0) {
            pricesMap.set(mintAddress.toLowerCase(), price);
          }
          
          // Small delay to avoid rate limiting
          await new Promise(resolve => setTimeout(resolve, 100));
        } catch (error) {
          console.warn(`[TokenList] Failed to fetch Solana price for ${token.symbol}:`, error);
        }
      }

      setSolanaTokenPrices(pricesMap);
      setSolanaPricesLoading(false);
    };

    fetchSolanaPrices();
  }, [chainFilter, tokens]);
  
  // Helper to get price for a token (handles Solana vs EVM)
  const getTokenPrice = useCallback((token: TokenDataWithCreator): number | null => {
    if (chainFilter === 'sol') {
      const mintAddress = (token as any).mint_address || token.contract_address;
      if (!mintAddress) return null;
      return solanaTokenPrices.get(mintAddress.toLowerCase()) ?? null;
    } else {
      return token.contract_address ? getPriceForToken(token.contract_address, chainId) : null;
    }
  }, [chainFilter, solanaTokenPrices, getPriceForToken, chainId]);
  
  // Helper to format market cap value
  const formatMarketCapValue = (value: number): string => {
    if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
    if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
    if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
    return `$${value.toFixed(0)}`;
  };
  
  // Helper to calculate market cap (handles Solana vs EVM)
  const getTokenMarketCap = useCallback((token: TokenDataWithCreator): string => {
    const price = getTokenPrice(token);
    if (!price) return '--';
    
    // For Solana tokens, calculate market cap from price * 1 billion supply (same as SolanaTokenList)
    if (chainFilter === 'sol') {
      const marketCap = price * 1_000_000_000; // 1 billion tokens
      return formatMarketCapValue(marketCap);
    } else {
      // For EVM tokens, formatMarketCap already multiplies by 1 billion
      return formatMarketCap(price);
    }
  }, [chainFilter, getTokenPrice]);
  // const { marketCapData } = useMarketCap(tokens);

  // Remove unused variables - comment out instead of removing to avoid breaking the component
  // const { data: marketData } = useMarketCap();
  // const { data: marketCapData } = useMarketCap();
  // const getBuyLink = (token: TokenData) => `https://dexscreener.com/ink/${token.contract_address}`;

  const getNetworkLogo = (dex?: string) => {
    // Show DEX-specific logo based on deployment destination
    if (dex === 'InkySwap') {
      return '/InkySwap.png';
    }
    // Default to Protofire (includes old tokens migrated to Protofire)
    return '/Protofire.png';
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };


  // Note: Removed formatVolume, formatMarketCap, and formatPriceWithSubscriptChange functions
  // since they were only used for DexScreener data which has been removed

  // Simple mapping to add favorite status - no more separate creator queries needed!
  const mapTokensWithDetails = useCallback(() => {
    const tokensWithDetails = tokens.map((token) => ({
      ...token,
      // creator_username is now directly available from the tokens table
      favorites_count: token.favorites_count || 0,
      is_favorited: token.id ? userFavorites.has(token.id) : false
    } as TokenDataWithCreator));

    setTokensWithDetails(tokensWithDetails);
  }, [tokens, userFavorites]);

  // Map tokens with details whenever tokens or favorites change
  useEffect(() => {
    mapTokensWithDetails();
  }, [mapTokensWithDetails]);

  // Server-side category filtering already applied via hook for Ink; apply client-side filtering for other chains
  let filteredTokens = tokensWithDetails;
  
  // Apply category filter for non-Ink chains (Ink already filtered server-side)
  if (chainFilter !== 'ink' && categoryFilter !== 'all') {
    filteredTokens = filteredTokens.filter(token => token.category === categoryFilter);
  }
  
  // Apply DEX filter (only relevant for Ink tokens)
  if (chainFilter === 'ink') {
    filteredTokens = filteredTokens.filter(token => {
      if (dexFilter === 'all') return true;
      return token.dex === dexFilter;
    });
  }

  // Handle favorite toggle
  const handleToggleFavorite = async (tokenId: string) => {
    console.log('🌟 Favorite toggle clicked!', { tokenId, isConnected, address });

    if (!isConnected) {
      console.log('❌ User not connected, cannot favorite');
      alert('Please connect your wallet to favorite tokens');
      return;
    }

    try {
      const isCurrentlyFavorited = userFavorites.has(tokenId);
      console.log('🔄 Toggling favorite:', { tokenId, isCurrentlyFavorited });

      const result = await toggleFavorite(tokenId, isCurrentlyFavorited);
      console.log('✅ Toggle result:', result);

      // Refresh the tokens with details to get updated favorite status
      mapTokensWithDetails();
    } catch (error) {
      console.error('Error toggling favorite:', error);
    }
  };

  // Handle quick buy
  const handleQuickBuy = (token: TokenDataWithCreator) => {
    console.log('💰 Quick buy clicked!', { token: token.symbol, isConnected, address, chainFilter });
    setSwapToken(token);
    
    // Use SolanaTradeModal for Solana tokens, regular TradeModal for EVM
    if (chainFilter === 'sol') {
      setSolanaSwapModalOpen(true);
    } else {
      setSwapModalOpen(true);
    }
  };



  if (tokensLoading) {
    return (
      <TokenListContainer>
        <SectionHeader>
          <SectionTitle>EXPLORE TOKENS</SectionTitle>
          <FilterSection>
            <FilterLabel>Filter by Category:</FilterLabel>
            <CategoryFilterContainer>
              <CategoryFilterScroll>
                <FilterButton
                  $active={categoryFilter === 'all'}
                  onClick={() => setCategoryFilter('all')}
                >
                  All
                </FilterButton>
                {(['Meme', 'DeFi', 'Utility', 'Gaming & Metaverse', 'SoFi', 'Creator Coins', 'AI & Tech', 'DAO', 'Experimental/Art', 'Kraken Girls'] as const).map((category) => (
                  <FilterButton
                    key={category}
                    $active={categoryFilter === category}
                    onClick={() => setCategoryFilter(category)}
                  >
                    {category}
                  </FilterButton>
                ))}
              </CategoryFilterScroll>
            </CategoryFilterContainer>
          </FilterSection>
        </SectionHeader>
        <LoadingMessage>LOADING TOKENS...</LoadingMessage>
      </TokenListContainer>
    );
  }

  if (tokensError) {
    return (
      <TokenListContainer>
        <SectionHeader>
          <SectionTitle>EXPLORE TOKENS</SectionTitle>
          <FilterSection>
            <FilterLabel>Filter by Category:</FilterLabel>
            <CategoryFilterContainer>
              <CategoryFilterScroll>
                <FilterButton
                  $active={categoryFilter === 'all'}
                  onClick={() => setCategoryFilter('all')}
                >
                  All
                </FilterButton>
                {(['Meme', 'DeFi', 'Utility', 'Gaming & Metaverse', 'SoFi', 'Creator Coins', 'AI & Tech', 'DAO', 'Experimental/Art', 'Kraken Girls'] as const).map((category) => (
                  <FilterButton
                    key={category}
                    $active={categoryFilter === category}
                    onClick={() => setCategoryFilter(category)}
                  >
                    {category}
                  </FilterButton>
                ))}
              </CategoryFilterScroll>
            </CategoryFilterContainer>
          </FilterSection>
        </SectionHeader>
        <ErrorMessage>ERROR LOADING TOKENS: {tokensError}</ErrorMessage>
      </TokenListContainer>
    );
  }

  return (
    <>
      <TokenListContainer>
        <SectionHeader>
          <SectionTitle>EXPLORE TOKENS</SectionTitle>
          <FilterSection>
            <FilterLabel>View:</FilterLabel>
            <ViewToggle>
              <ViewButton $active={viewMode === 'cards'} onClick={() => setViewMode('cards')}>
                Cards
              </ViewButton>
              <ViewButton $active={viewMode === 'list'} onClick={() => setViewMode('list')}>
                List
              </ViewButton>
            </ViewToggle>
          </FilterSection>
          <FilterSection>
            <FilterLabel>Filter by DEX:</FilterLabel>
            <CategoryFilterContainer>
              <FilterButton
                $active={dexFilter === 'all'}
                onClick={() => setDexFilter('all')}
              >
                All DEXs
              </FilterButton>
              <FilterButton
                $active={dexFilter === 'Protofire'}
                onClick={() => setDexFilter('Protofire')}
              >
                <FilterButtonLogo src="/Protofire.png" alt="Protofire" />
                Protofire
              </FilterButton>
              <FilterButton
                $active={dexFilter === 'InkySwap'}
                onClick={() => setDexFilter('InkySwap')}
              >
                <FilterButtonLogo src="/InkySwap.png" alt="InkySwap" />
                InkySwap
              </FilterButton>
            </CategoryFilterContainer>
          </FilterSection>
          <FilterSection>
            <FilterLabel>Filter by Category:</FilterLabel>
            <CategoryFilterContainer>
              <CategoryFilterScroll>
                <FilterButton
                  $active={categoryFilter === 'all'}
                  onClick={() => setCategoryFilter('all')}
                >
                  All
                </FilterButton>
                {(['Meme', 'DeFi', 'Utility', 'Gaming & Metaverse', 'SoFi', 'Creator Coins', 'AI & Tech', 'DAO', 'Experimental/Art', 'Kraken Girls'] as const).map((category) => (
                  <FilterButton
                    key={category}
                    $active={categoryFilter === category}
                    onClick={() => setCategoryFilter(category)}
                  >
                    {category}
                  </FilterButton>
                ))}
              </CategoryFilterScroll>
            </CategoryFilterContainer>
          </FilterSection>
        </SectionHeader>


        {/* Remove SearchContainer here, it is now in the parent */}

        {viewMode === 'cards' ? (
          <TokenGrid>
            {filteredTokens.map((token, index) => {
              // Chain-specific explorer, DEXTools, and DexScreener links
              const explorerLinkPrefix = 'https://explorer.inkonchain.com';
              const dextoolsLink = token.contract_address
                ? `https://www.dextools.io/app/en/ink/pair-explorer/${token.contract_address}`
                : '#';
              // Note: Removed DexScreener link since we no longer use DexScreener data
              // Get real-time price data (handles Solana vs EVM)
              const priceUSD = getTokenPrice(token);
              const change24h = chainFilter !== 'sol' && token.contract_address 
                ? get24hChangeForToken(token.contract_address, chainId) 
                : null;

              // Find TVL for this token
              // let tvl = 0;
              // if (tvlData && token.contract_address) {
              //   const contributor = tvlData.contributors.find(
              //     (c) => c.address && c.address.toLowerCase() === token.contract_address!.toLowerCase()
              //   );
              //   if (contributor) tvl = contributor.value;
              // }

              // Get DexScreener data for this token
              // const dexScreenerData = token.contract_address ? getTokenData(token.contract_address) : null;

              // Create DexScreener link using pair address from DexScreener data
              // const dexScreenerLink = ... (remove this line)

              return (
                <TokenCard key={index}>
                  <TokenCardContent>
                    <TokenLogoClickable
                      src={ensureHttpsUrl(token.logo_url, 'https://placehold.co/60x60/333333/AAAAAA?text=Logo')}
                      alt={`${token.name} Logo`}
                      onClick={() => {
                        const path = window.location.pathname.startsWith('/pwa')
                          ? `/pwa/token/${token.contract_address}`
                          : `/token/${token.contract_address}`;
                        navigate(path);
                      }}
                      title="View full token details"
                    />
                    <TokenInfoWrapper>
                      <TokenNameRow>
                        <div style={{ display: 'flex', alignItems: 'center', minWidth: 0, flex: 1, gap: '8px' }}>
                          <TokenNameSpan>
                            {token.name}
                          </TokenNameSpan>
                          <NetworkLogo src={getNetworkLogo(token.dex)} alt={token.dex || 'Protofire'} title={`Deployed on ${token.dex || 'Protofire'} DEX`} style={{ flexShrink: 0 }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                          <QuickBuyButton
                            onClick={() => handleQuickBuy(token)}
                            title="Quick buy this token"
                          >
                            Trade
                          </QuickBuyButton>
                          <FavoriteButton
                            $isFavorited={token.is_favorited || false}
                            onClick={() => handleToggleFavorite(token.id!)}
                            title={token.is_favorited ? "Remove from favorites" : "Add to favorites"}
                          >
                            <i className={`fas fa-star`} />
                          </FavoriteButton>
                        </div>
                      </TokenNameRow>
                      <SymbolContractRow>
                        <TokenSymbol>${token.symbol.toLowerCase()}</TokenSymbol>
                        <span style={{
                          color: 'rgba(255, 255, 255, 0.4)',
                          fontSize: '12px',
                          fontFamily: 'monospace',
                          fontWeight: 500
                        }}>
                          {token.contract_address ? `${token.contract_address.slice(0, 6)}...${token.contract_address.slice(-4)}` : 'Not available'}
                        </span>
                        {token.contract_address && (
                          <CopyButton
                            onClick={() => copyToClipboard(token.contract_address!)}
                            title="Copy contract address"
                          >
                            <i className="fas fa-copy" style={{ fontSize: '11px' }}></i>
                          </CopyButton>
                        )}
                      </SymbolContractRow>

                      {/* Creator info and Market Cap on same line */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        marginTop: '2px',
                        marginBottom: '4px',
                        gap: '12px'
                      }}>
                        {/* Creator info on the left */}
                        {token.deployer_address ? (
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            minWidth: 0,
                            flex: 1
                          }}>
                            <img
                              src="/social_icons/dev_icon.png"
                              alt="Developer"
                              style={{ width: '14px', height: '14px', flexShrink: 0, opacity: 0.7 }}
                            />
                            <ZNSAddressDisplay
                              address={token.deployer_address}
                              href={`${explorerLinkPrefix}/address/${token.deployer_address}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              showIcon={true}
                              iconSize={11}
                              truncateLength={4}
                              enabled={chainFilter === 'ink'}
                              style={{
                                color: 'rgba(255, 255, 255, 0.5)',
                                fontSize: '11px',
                                fontFamily: 'Inter, sans-serif',
                                textDecoration: 'none',
                                transition: 'all 0.2s ease'
                              }}
                              onMouseEnter={(e) => {
                                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(168, 85, 247, 0.8)';
                              }}
                              onMouseLeave={(e) => {
                                (e.currentTarget as HTMLAnchorElement).style.color = 'rgba(255, 255, 255, 0.5)';
                              }}
                            />
                          </div>
                        ) : (
                          <div style={{ flex: 1 }} />
                        )}

                        {/* Market Cap and 24h Change on the right */}
                        <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                          {priceUSD ? (
                            <p style={{
                              color: '#ffffff',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '13px',
                              fontWeight: '600',
                              margin: 0,
                              letterSpacing: '0.01em',
                              whiteSpace: 'nowrap'
                            }}>
                              MC: {getTokenMarketCap(token)}
                            </p>
                          ) : (
                            <p style={{
                              color: 'rgba(255, 255, 255, 0.4)',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '13px',
                              fontWeight: '500',
                              margin: 0,
                              whiteSpace: 'nowrap'
                            }}>
                              MC: --
                            </p>
                          )}
                          {change24h !== null && (
                            <span style={{
                              color: change24h >= 0 ? '#10b981' : '#ef4444',
                              fontFamily: 'Inter, sans-serif',
                              fontSize: '11px',
                              fontWeight: '500',
                            }}>
                              {formatPriceChange(change24h)}
                            </span>
                          )}
                        </div>
                      </div>
                    </TokenInfoWrapper>
                  </TokenCardContent>

                  {/* Social Media Links & Actions - Full Width Bottom Section */}
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start',
                    width: '100%',
                    paddingTop: '10px',
                    borderTop: '1px solid #000000'
                  }}>
                    {/* Social Media Links */}
                    {sanitizeUrl(token.website) && (
                      <TokenLinkIcon href={sanitizeUrl(token.website)!} target="_blank" title="Website">
                        <i className="fas fa-globe"></i>
                      </TokenLinkIcon>
                    )}
                    {sanitizeUrl(token.twitter) && (
                      <TokenLinkIcon href={sanitizeUrl(token.twitter)!} target="_blank" title="Twitter/X">
                        <i className="fab fa-twitter"></i>
                      </TokenLinkIcon>
                    )}
                    {sanitizeUrl(token.telegram) && (
                      <TokenLinkIcon href={sanitizeUrl(token.telegram)!} target="_blank" title="Telegram">
                        <i className="fab fa-telegram-plane"></i>
                      </TokenLinkIcon>
                    )}
                    {sanitizeUrl(token.discord) && (
                      <TokenLinkIcon href={sanitizeUrl(token.discord)!} target="_blank" title="Discord">
                        <i className="fab fa-discord"></i>
                      </TokenLinkIcon>
                    )}

                    {/* Spacer to push explorer links to the right */}
                    <div style={{ flex: 1 }}></div>

                    {/* Explorer Links */}
                    {token.contract_address && (
                      <TokenLinkIcon href={`${explorerLinkPrefix}/address/${token.contract_address}`} target="_blank" title="View on Explorer">
                        <ExplorerIcon src="/Ink_Contract.png" alt="Explorer" />
                      </TokenLinkIcon>
                    )}
                    {token.contract_address && (
                      <TokenLinkIcon href={dextoolsLink} target="_blank" title="View on DEXTools">
                        <DextoolsIcon src="/dextoolswhite.png" alt="DEXTools" />
                      </TokenLinkIcon>
                    )}

                    {/* View Details */}
                    <ViewDetailsButton onClick={() => {
                      const path = window.location.pathname.startsWith('/pwa')
                        ? `/pwa/token/${token.contract_address}`
                        : `/token/${token.contract_address}`;
                      navigate(path);
                    }} title="View Details">
                      <i className="fas fa-arrow-up-right-from-square"></i>
                    </ViewDetailsButton>
                  </div>
                </TokenCard>
              );
            })}
          </TokenGrid>
        ) : (
          <TokenListTable>
            <TokenListHeader>
              <div>#</div>
              <div></div>
              <div>TOKEN</div>
              <div style={{ textAlign: 'right' }}>
                <span className="desktop-only">MARKET CAP</span>
                <span className="mobile-only">MC</span>
              </div>
              <div style={{ textAlign: 'right' }}>
                <span className="desktop-only">PRICE</span>
                <span className="mobile-only">$</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <span className="desktop-only">TRADE</span>
                <span className="mobile-only">•</span>
              </div>
              <div style={{ textAlign: 'center' }}>
                <span className="desktop-only">FAV</span>
                <span className="mobile-only">★</span>
              </div>
            </TokenListHeader>
            {filteredTokens.map((token, index) => {
              const priceUSD = getTokenPrice(token);
              const change24h = chainFilter !== 'sol' && token.contract_address 
                ? get24hChangeForToken(token.contract_address, chainId) 
                : null;
              const marketCap = getTokenMarketCap(token);
              const isFavorited = token.is_favorited || false;

              return (
                <TokenListRow key={index}>
                  <TokenRankCell>{index + 1 + (currentPage - 1) * 20}</TokenRankCell>
                  <TokenListLogo
                    src={ensureHttpsUrl(token.logo_url, 'https://placehold.co/32x32/333333/AAAAAA?text=Logo')}
                    alt={`${token.name} Logo`}
                    onClick={() => {
                      const path = window.location.pathname.startsWith('/pwa')
                        ? `/pwa/token/${token.contract_address}`
                        : `/token/${token.contract_address}`;
                      navigate(path);
                    }}
                  />
                  <TokenListInfoCell onClick={() => {
                    const path = window.location.pathname.startsWith('/pwa')
                      ? `/pwa/token/${token.contract_address}`
                      : `/token/${token.contract_address}`;
                    navigate(path);
                  }}>
                    <TokenListName>{token.name}</TokenListName>
                    <TokenListSymbol>${token.symbol}</TokenListSymbol>
                  </TokenListInfoCell>
                  <TokenListValueCell>{marketCap}</TokenListValueCell>
                  <TokenListValueCell>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                      <span>{formatPriceWithSubscript(priceUSD)}</span>
                      {change24h !== null && (
                        <span style={{ 
                          color: change24h >= 0 ? '#10b981' : '#ef4444', 
                          fontSize: '10px',
                          fontWeight: 500 
                        }}>
                          {formatPriceChange(change24h)}
                        </span>
                      )}
                    </div>
                  </TokenListValueCell>
                  <TokenListDataCell>
                    <QuickBuyButton
                      onClick={(e) => {
                        e.stopPropagation();
                        handleQuickBuy(token);
                      }}
                      title="Quick buy"
                      style={{
                        padding: window.innerWidth <= 768 ? '2px 4px' : '4px 8px',
                        fontSize: window.innerWidth <= 768 ? '8px' : '11px',
                        margin: '0 auto',
                        minWidth: window.innerWidth <= 768 ? '38px' : 'auto',
                        height: window.innerWidth <= 768 ? '20px' : 'auto'
                      }}
                    >
                      Trade
                    </QuickBuyButton>
                  </TokenListDataCell>
                  <TokenListDataCell>
                    <FavoriteButton
                      onClick={(e) => {
                        e.stopPropagation();
                        if (token.id) {
                          handleToggleFavorite(token.id);
                        }
                      }}
                      $isFavorited={isFavorited}
                      title={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                      style={{
                        minWidth: window.innerWidth <= 768 ? '28px' : 'auto',
                        height: window.innerWidth <= 768 ? '20px' : 'auto',
                        padding: window.innerWidth <= 768 ? '2px' : '6px',
                        fontSize: window.innerWidth <= 768 ? '10px' : '16px'
                      }}
                    >
                      <i className="fas fa-star" />
                    </FavoriteButton>
                  </TokenListDataCell>
                </TokenListRow>
              );
            })}
          </TokenListTable>
        )}

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <PaginationContainer>
            <PaginationButton
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              title="Previous Page"
            >
              ←
            </PaginationButton>

            <PageNumbers>
              {(() => {
                const pages = [];
                const maxVisiblePages = 5;
                let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                // Adjust start page if we're near the end
                if (endPage - startPage + 1 < maxVisiblePages) {
                  startPage = Math.max(1, endPage - maxVisiblePages + 1);
                }

                // Add first page and ellipsis if needed
                if (startPage > 1) {
                  pages.push(
                    <PageNumberButton key={1} onClick={() => goToPage(1)} $active={currentPage === 1}>
                      1
                    </PageNumberButton>
                  );
                  if (startPage > 2) {
                    pages.push(<span key="ellipsis1" style={{ color: '#bdbdbd', padding: '0 5px' }}>...</span>);
                  }
                }

                // Add visible page numbers
                for (let i = startPage; i <= endPage; i++) {
                  pages.push(
                    <PageNumberButton
                      key={i}
                      onClick={() => goToPage(i)}
                      $active={currentPage === i}
                    >
                      {i}
                    </PageNumberButton>
                  );
                }

                // Add last page and ellipsis if needed
                if (endPage < totalPages) {
                  if (endPage < totalPages - 1) {
                    pages.push(<span key="ellipsis2" style={{ color: '#bdbdbd', padding: '0 5px' }}>...</span>);
                  }
                  pages.push(
                    <PageNumberButton
                      key={totalPages}
                      onClick={() => goToPage(totalPages)}
                      $active={currentPage === totalPages}
                    >
                      {totalPages}
                    </PageNumberButton>
                  );
                }

                return pages;
              })()}
            </PageNumbers>

            <PaginationButton
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              title="Next Page"
            >
              →
            </PaginationButton>

            <PaginationInfo>
              Page {currentPage} of {totalPages} (last 100 tokens displayed)
            </PaginationInfo>
          </PaginationContainer>
        )}

      </TokenListContainer>

      {/* Trade Modal - Opens CustomSwapWidget with pre-populated output token */}
      <TradeModal
        isOpen={swapModalOpen}
        onClose={() => {
          setSwapModalOpen(false);
          setSwapToken(null);
        }}
        token={swapToken ? {
          address: swapToken.contract_address || '',
          symbol: swapToken.symbol,
          name: swapToken.name,
          chainId: chainIdMap[chainFilter] || 57073,
          logo: ensureHttpsUrl(swapToken.logo_url, ''),
          decimals: 18,
        } : null}
      />
      
      {/* Solana Trade Modal - Direct Orca Whirlpool swaps */}
      <SolanaWalletProvider>
        <SolanaTradeModal
          isOpen={solanaSwapModalOpen}
          onClose={() => {
            setSolanaSwapModalOpen(false);
            setSwapToken(null);
          }}
          token={swapToken ? {
            address: swapToken.contract_address || (swapToken as any).mint_address || '',
            symbol: swapToken.symbol,
            name: swapToken.name,
            logo: ensureHttpsUrl(swapToken.logo_url, ''),
            decimals: 9,
            poolAddress: (swapToken as any).pool_address || (swapToken as any).whirlpool_address,
            // Cached pool data (reduces RPC calls)
            tokenVaultA: (swapToken as any).token_vault_a,
            tokenVaultB: (swapToken as any).token_vault_b,
            tickSpacing: (swapToken as any).tick_spacing,
          } : null}
        />
      </SolanaWalletProvider>
    </>
  );
};

export default TokenList;

